import React, { useEffect, useMemo, useRef, useState } from 'react';
import { searchService } from '../services/api';
import { Loader2, ExternalLink, Search, MapPin, FileText, ShoppingBag, Newspaper } from 'lucide-react';

const sectionMeta = {
  page: { label: 'Pages', icon: FileText },
  product: { label: 'Products', icon: ShoppingBag },
  dealer: { label: 'Dealers', icon: MapPin },
  blog: { label: 'Media', icon: Newspaper },
};

const exampleQueries = ['About', 'Investor', 'Distributor', 'Dealer', 'Sustainability'];

function SearchPalette({ open, onClose }) {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState({ pages: [], products: [], dealers: [], blogs: [] });
  const [activeIndex, setActiveIndex] = useState(0);
  const [error, setError] = useState('');
  const inputRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
      const handler = (e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          onClose();
        }
      };
      window.addEventListener('keydown', handler);
      return () => window.removeEventListener('keydown', handler);
    }
    return undefined;
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return undefined;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (!query.trim()) {
      setResults({ pages: [], products: [], dealers: [], blogs: [] });
      setLoading(false);
      return undefined;
    }
    timeoutRef.current = setTimeout(async () => {
      setLoading(true);
      setError('');
      try {
        const data = await searchService.query(query);
        setResults(data.results || { pages: [], products: [], dealers: [], blogs: [] });
        setActiveIndex(0);
      } catch (err) {
        setError('Unable to search right now.');
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => timeoutRef.current && clearTimeout(timeoutRef.current);
  }, [query, open]);

  const flattenedResults = useMemo(() => {
    const flat = [];
    Object.entries(results || {}).forEach(([type, items]) => {
      items?.forEach((item) => flat.push({ ...item, type }));
    });
    return flat;
  }, [results]);

  useEffect(() => {
    const handler = (e) => {
      if (!open) return;
      if (['ArrowDown', 'ArrowUp', 'Enter'].includes(e.key)) {
        e.preventDefault();
      }
      if (e.key === 'ArrowDown') {
        setActiveIndex((prev) => Math.min(prev + 1, flattenedResults.length - 1));
      } else if (e.key === 'ArrowUp') {
        setActiveIndex((prev) => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter' && flattenedResults[activeIndex]) {
        window.open(flattenedResults[activeIndex].url, '_self');
        onClose();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [activeIndex, flattenedResults, onClose, open]);

  const renderResult = (result, index) => {
    const meta = sectionMeta[result.type] || { icon: Search, label: result.type };
    const Icon = meta.icon;
    const isActive = index === activeIndex;
    return (
      <button
        key={`${result.type}-${index}`}
        className={`w-full text-left px-4 py-3 rounded-2xl border ${isActive ? 'border-red-400 bg-red-50/60 text-red-900' : 'border-slate-100 bg-white text-slate-900'} flex items-center gap-4 transition`}
        onMouseEnter={() => setActiveIndex(index)}
        onClick={() => {
          window.open(result.url, '_self');
          onClose();
        }}
      >
        <Icon size={18} className={isActive ? 'text-red-600' : 'text-slate-400'} />
        <div className="flex-1">
          <div className="text-sm font-semibold truncate">{result.title}</div>
          {result.excerpt && <div className="text-xs text-slate-500 line-clamp-1">{result.excerpt}</div>}
          {result.meta && (
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 mt-1 flex gap-2">
              {Object.entries(result.meta)
                .filter(([, value]) => Boolean(value))
                .map(([key, value]) => (
                  <span key={key}>{value}</span>
                ))}
            </div>
          )}
        </div>
        <ExternalLink size={14} className={isActive ? 'text-red-500' : 'text-slate-400'} />
      </button>
    );
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/80 backdrop-blur-sm flex items-start justify-center px-4 py-16" onClick={onClose}>
      <div className="w-full max-w-3xl bg-white rounded-3xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 border-b border-slate-100 px-6 py-4">
          <Search className="text-slate-400" size={18} />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search pages, products, dealers, media..."
            className="flex-1 text-base focus:outline-none"
          />
          <kbd className="px-2 py-1 text-[10px] bg-slate-100 rounded">ESC</kbd>
        </div>
        {loading && (
          <div className="flex items-center gap-2 px-6 py-4 text-slate-500 text-sm">
            <Loader2 className="animate-spin" size={16} />
            Searching...
          </div>
        )}
        {error && <div className="px-6 py-3 text-sm text-red-500">{error}</div>}
        {!loading && flattenedResults.length === 0 && !query && (
          <div className="px-6 py-4 text-slate-500 text-sm">Try searching for:</div>
        )}
        {!loading && flattenedResults.length === 0 && !query && (
          <div className="px-6 pb-6 flex flex-wrap gap-2">
            {exampleQueries.map((chip) => (
              <button
                key={chip}
                onClick={() => setQuery(chip)}
                className="px-3 py-1 rounded-full bg-slate-100 text-xs text-slate-600 hover:bg-red-50 hover:text-red-700"
              >
                {chip}
              </button>
            ))}
          </div>
        )}
        {!loading && flattenedResults.length === 0 && query && !error && (
          <div className="px-6 py-4 text-sm text-slate-500">No results found.</div>
        )}
        {!loading && flattenedResults.length > 0 && (
          <div className="px-6 py-4 space-y-2 max-h-[420px] overflow-y-auto">
            {flattenedResults.map((result, index) => renderResult(result, index))}
          </div>
        )}
      </div>
    </div>
  );
}

export default SearchPalette;
